'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useBodyScrollLock } from './hooks/useBodyScrollLock';
import { useFocusTrap } from './hooks/useFocusTrap';

type ModalConfig = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  closeButtonLabel?: string;
  className?: string;
  panelClassName?: string;
  backdropClassName?: string;
  autoCloseOnSuccessMs?: number | false;
};

type DynamicFormModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modal?: ModalConfig;
  children: React.ReactNode;
};

export function DynamicFormModal({
  open,
  onOpenChange,
  modal,
  children,
}: DynamicFormModalProps) {
  const [isClosing, setIsClosing] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isEntering, setIsEntering] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const {
    closeOnBackdrop = true,
    closeOnEscape = true,
    closeButtonLabel = 'Close dialog',
    className = '',
    panelClassName = '',
    backdropClassName = '',
  } = modal ?? {};

  useEffect(() => {
    if (open) {
      setIsMounted(true);
      setIsClosing(false);
    } else if (isMounted) {
      setIsClosing(true);
    }
  }, [open, isMounted]);

  useEffect(() => {
    if (isMounted && !isClosing) {
      const timer = setTimeout(() => setIsEntering(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsEntering(false);
    }
  }, [isMounted, isClosing]);

  useEffect(() => {
    if (isClosing) {
      const timer = setTimeout(() => {
        setIsMounted(false);
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isClosing]);

  const handleTransitionEnd = useCallback(() => {
    if (isClosing) {
      setIsMounted(false);
    }
  }, [isClosing]);

  useEffect(() => {
    if (isMounted && !isClosing && panelRef.current) {
      const closeButton = panelRef.current.querySelector('[data-modal-close]');
      if (closeButton instanceof HTMLElement) {
        closeButton.focus();
      }
    }
  }, [isMounted, isClosing]);

  useEffect(() => {
    if (!isMounted || isClosing || !closeOnEscape) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMounted, isClosing, closeOnEscape, onOpenChange]);

  useBodyScrollLock(isMounted && !isClosing);
  useFocusTrap(isMounted && !isClosing, panelRef);

  const titleId = modal?.title ? 'dynamic-form-modal-title' : undefined;
  const descId = modal?.description
    ? 'dynamic-form-modal-description'
    : undefined;

  if (!isMounted) return null;

  const content = (
    <div
      className={`fixed inset-0 z-900 flex items-center justify-center ${className}`}
    >
      <div
        className={`absolute inset-0 bg-black/50 transition-opacity duration-300 motion-reduce:transition-none ${
          isClosing ? 'opacity-0' : 'opacity-100'
        } ${backdropClassName}`}
        onClick={() => {
          if (closeOnBackdrop) {
            onOpenChange(false);
          }
        }}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className={`relative z-10 max-h-[90vh] w-[calc(100vw-2rem)] max-w-xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl transition-transform duration-300 ease-out motion-reduce:transition-none ${
          isEntering && !isClosing ? 'translate-x-0' : 'translate-x-[-120%]'
        } ${panelClassName}`}
        onTransitionEnd={handleTransitionEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          data-modal-close
          onClick={() => onOpenChange(false)}
          aria-label={closeButtonLabel}
          className="focus:ring-royal-300 absolute top-4 right-4 flex size-8 cursor-pointer items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:ring-2 focus:outline-none"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="size-5"
            aria-hidden="true"
          >
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
          </svg>
        </button>

        {modal?.title && (
          <h2
            id="dynamic-form-modal-title"
            className="mb-1 pr-10 text-lg font-semibold text-gray-900"
          >
            {modal.title}
          </h2>
        )}

        {modal?.description && (
          <p
            id="dynamic-form-modal-description"
            className="mb-4 pr-10 text-sm text-gray-500"
          >
            {modal.description}
          </p>
        )}

        <div className="mt-2">{children}</div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(content, document.body);
  }

  return null;
}
